import React, { useState, useEffect } from 'react';
import { X, Send, User, Users, AlertTriangle, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ComplianceReminderModal = ({
    isOpen,
    onClose,
    taskId,
    taskTitle,
    employeeIds = [],
    employeeName,
    bulkMode = false
}) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [incompleteEmployees, setIncompleteEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState(employeeIds);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && bulkMode && taskId) {
            fetchIncompleteEmployees();
        }
    }, [isOpen, bulkMode, taskId]);

    useEffect(() => {
        // Set default message
        setMessage(`⚠️ **Compliance Reminder**

You have an outstanding compliance task: **${taskTitle}**

This task is required for legal/regulatory compliance and must be completed as soon as possible.

Please log in to your dashboard and complete this task today.

If you have any questions or need assistance, please reach out to your manager or HR.`);
    }, [taskTitle]);

    const fetchIncompleteEmployees = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/compliance/tasks/${taskId}/stats`);
            const incomplete = response.data.stats.employees.filter(emp => emp.status !== 'completed');
            setIncompleteEmployees(incomplete);
            setSelectedEmployees(incomplete.map(emp => emp.employee_id));
        } catch (error) {
            console.error('Error fetching incomplete employees:', error);
            toast.error('Failed to load employee list');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectEmployee = (employeeId) => {
        setSelectedEmployees(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const handleSelectAll = () => {
        if (selectedEmployees.length === incompleteEmployees.length) {
            setSelectedEmployees([]);
        } else {
            setSelectedEmployees(incompleteEmployees.map(emp => emp.employee_id));
        }
    };

    const handleSend = async () => {
        if (selectedEmployees.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }

        if (!message.trim()) {
            toast.error('Please enter a message');
            return;
        }

        setSending(true);
        const loadingToast = toast.loading('Sending reminder...');

        try {
            await api.post('/compliance/reminder', {
                employee_ids: selectedEmployees,
                task_id: taskId,
                message: message.trim()
            });

            toast.success(
                `Reminder sent to ${selectedEmployees.length} employee${selectedEmployees.length !== 1 ? 's' : ''}!`,
                { id: loadingToast }
            );
            onClose();
        } catch (error) {
            console.error('Error sending reminder:', error);
            toast.error('Failed to send reminder', { id: loadingToast });
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-all">
            <div className="bg-white rounded-none md:rounded-xl shadow-2xl max-w-2xl w-full h-full md:h-auto md:max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between safe-area-top">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <MessageSquare className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Send Compliance Reminder</h2>
                            <p className="text-sm text-gray-500">
                                {bulkMode
                                    ? `Remind employees about: ${taskTitle}`
                                    : `Send reminder to ${employeeName || 'employee'}`
                                }
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {/* Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">Compliance Task Reminder</p>
                                <p className="text-sm text-amber-700 mt-1">
                                    This will send a message through the Messages system to remind the selected employee(s) about the compliance task.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Employee Selection (Bulk Mode) */}
                    {bulkMode && (
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Select Employees ({selectedEmployees.length} selected)
                                </label>
                                <button
                                    onClick={handleSelectAll}
                                    className="text-sm text-primary hover:text-primary-hover font-medium"
                                >
                                    {selectedEmployees.length === incompleteEmployees.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                </div>
                            ) : (
                                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                                    {incompleteEmployees.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500">
                                            No incomplete employees found
                                        </div>
                                    ) : (
                                        incompleteEmployees.map((emp) => (
                                            <label
                                                key={emp.employee_id}
                                                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEmployees.includes(emp.employee_id)}
                                                    onChange={() => handleSelectEmployee(emp.employee_id)}
                                                    className="w-4 h-4 text-primary rounded focus:ring-primary"
                                                />
                                                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                                    <User className="w-4 h-4 text-gray-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-800 text-sm">
                                                        {emp.first_name} {emp.last_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{emp.email}</p>
                                                </div>
                                                {emp.days_overdue > 0 && (
                                                    <span className="text-xs text-red-600 font-medium">
                                                        {Math.round(emp.days_overdue)} days overdue
                                                    </span>
                                                )}
                                            </label>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Single Employee Display */}
                    {!bulkMode && employeeName && (
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Recipient
                            </label>
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                    <User className="w-5 h-5 text-gray-500" />
                                </div>
                                <span className="font-medium text-gray-800">{employeeName}</span>
                            </div>
                        </div>
                    )}

                    {/* Message */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Message
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows="8"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                            placeholder="Enter your reminder message..."
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            Supports **bold** and basic markdown formatting
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 md:px-6 py-4 flex items-center justify-end gap-3 safe-area-bottom">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={sending || selectedEmployees.length === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sending ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Send Reminder
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComplianceReminderModal;

